/**
 * DNA Calendário Escolar - Painel Administrativo
 * Gerenciamento completo de eventos do calendário escolar
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Calendar, CalendarDays, List, Grid3X3, Plus, Search, Filter,
  RefreshCw, Star, Edit, Trash2, ChevronLeft, ChevronRight,
  GraduationCap, PartyPopper, Umbrella, FileText, Clock,
  Users, Download, Eye, CheckCircle, X, MapPin, Bell
} from 'lucide-react';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CATEGORIA_CONFIG = {
  eventos_escolares: { 
    label: 'Eventos Escolares', 
    bg: 'bg-purple-500', 
    text: 'text-white',
    bgLight: 'bg-purple-100',
    textLight: 'text-purple-700',
    border: 'border-purple-500',
    icon: PartyPopper 
  },
  feriados: { 
    label: 'Feriados', 
    bg: 'bg-green-500', 
    text: 'text-white',
    bgLight: 'bg-green-100',
    textLight: 'text-green-700',
    border: 'border-green-500',
    icon: Calendar 
  },
  atividades_suspensas: { 
    label: 'Atividades Suspensas / Férias', 
    bg: 'bg-orange-500', 
    text: 'text-white',
    bgLight: 'bg-orange-100',
    textLight: 'text-orange-700',
    border: 'border-orange-500',
    icon: Umbrella 
  },
  avaliacoes: { 
    label: 'Avaliações / APA / Recuperação', 
    bg: 'bg-blue-500', 
    text: 'text-white',
    bgLight: 'bg-blue-100',
    textLight: 'text-blue-700',
    border: 'border-blue-500',
    icon: FileText 
  }
};

const PUBLICO_OPTIONS = [
  { value: 'educacao_infantil', label: 'Educação Infantil' },
  { value: 'fundamental_i', label: 'Fundamental I' },
  { value: 'fundamental_ii', label: 'Fundamental II' },
  { value: 'ensino_medio', label: 'Ensino Médio' },
  { value: 'familias', label: 'Famílias' },
  { value: 'geral', label: 'Geral' }
];

export default function CalendarioEscolarAdmin() {
  // Estados principais
  const [activeTab, setActiveTab] = useState('calendario');
  const [viewMode, setViewMode] = useState('mes'); // mes, lista, cards
  const [eventos, setEventos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estados de navegação
  const [anoAtual, setAnoAtual] = useState(2026);
  const [mesAtual, setMesAtual] = useState(new Date().getMonth() + 1);
  
  // Estados de filtro
  const [filtroCategoria, setFiltroCategoria] = useState('all');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroDestaque, setFiltroDestaque] = useState(false);
  
  // Estados de modal
  const [showNovoEvento, setShowNovoEvento] = useState(false);
  const [showEditarEvento, setShowEditarEvento] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  
  // Estados do formulário
  const [formEvento, setFormEvento] = useState({
    titulo: '',
    data_inicio: '',
    data_fim: '',
    categoria: 'eventos_escolares',
    descricao: '',
    publico_alvo: ['geral'],
    destaque: false,
    local: '',
    horario: ''
  });
  
  // Carregar eventos
  const carregarEventos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('ano', anoAtual);
      if (viewMode === 'mes') {
        params.append('mes', mesAtual);
      }
      if (filtroCategoria !== 'all') {
        params.append('categoria', filtroCategoria);
      }
      if (filtroBusca) {
        params.append('busca', filtroBusca);
      }
      
      const res = await api.get(`/calendario/eventos?${params.toString()}`);
      setEventos(res.data.eventos || []);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, [anoAtual, mesAtual, viewMode, filtroCategoria, filtroBusca]);
  
  const carregarEstatisticas = useCallback(async () => {
    try {
      const res = await api.get(`/calendario/estatisticas?ano=${anoAtual}`);
      setEstatisticas(res.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [anoAtual]);
  
  useEffect(() => {
    carregarEventos();
    carregarEstatisticas();
  }, [carregarEventos, carregarEstatisticas]);
  
  // Popular calendário 2026
  const handlePopularCalendario = async () => {
    try {
      setLoading(true);
      const res = await api.post('/calendario/popular-2026');
      toast.success(res.data.message);
      carregarEventos();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao popular calendário:', error);
      toast.error('Erro ao popular calendário');
    } finally {
      setLoading(false);
    }
  };
  
  // Criar evento
  const handleCriarEvento = async () => {
    if (!formEvento.titulo || !formEvento.data_inicio) {
      toast.error('Preencha título e data de início');
      return;
    }
    
    try {
      await api.post('/calendario/eventos', formEvento);
      toast.success('Evento criado com sucesso!');
      setShowNovoEvento(false);
      resetForm();
      carregarEventos();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      toast.error('Erro ao criar evento');
    }
  };
  
  // Atualizar evento
  const handleAtualizarEvento = async () => {
    if (!eventoSelecionado) return;
    
    try {
      await api.put(`/calendario/eventos/${eventoSelecionado.id}`, formEvento);
      toast.success('Evento atualizado!');
      setShowEditarEvento(false);
      carregarEventos();
    } catch (error) {
      console.error('Erro ao atualizar evento:', error);
      toast.error('Erro ao atualizar evento');
    }
  };
  
  // Deletar evento
  const handleDeletarEvento = async (eventoId) => {
    if (!confirm('Tem certeza que deseja remover este evento?')) return;
    
    try {
      await api.delete(`/calendario/eventos/${eventoId}`);
      toast.success('Evento removido');
      carregarEventos();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao deletar evento:', error);
      toast.error('Erro ao deletar evento');
    }
  };
  
  // Toggle destaque
  const handleToggleDestaque = async (evento) => {
    try {
      await api.post(`/calendario/eventos/${evento.id}/destaque?destaque=${!evento.destaque}`);
      toast.success(evento.destaque ? 'Destaque removido' : 'Evento destacado!');
      carregarEventos();
    } catch (error) {
      console.error('Erro ao alterar destaque:', error);
      toast.error('Erro ao alterar destaque');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormEvento({
      titulo: '',
      data_inicio: '',
      data_fim: '',
      categoria: 'eventos_escolares',
      descricao: '',
      publico_alvo: ['geral'],
      destaque: false,
      local: '',
      horario: ''
    });
  };
  
  // Abrir edição
  const abrirEdicao = (evento) => {
    setEventoSelecionado(evento);
    setFormEvento({
      titulo: evento.titulo,
      data_inicio: evento.data_inicio,
      data_fim: evento.data_fim || '',
      categoria: evento.categoria,
      descricao: evento.descricao || '',
      publico_alvo: evento.publico_alvo || ['geral'],
      destaque: evento.destaque || false,
      local: evento.local || '',
      horario: evento.horario || ''
    });
    setShowEditarEvento(true);
  };
  
  // Navegação de mês
  const mesAnterior = () => {
    if (mesAtual === 1) {
      setMesAtual(12);
      setAnoAtual(anoAtual - 1);
    } else {
      setMesAtual(mesAtual - 1);
    }
  };
  
  const proximoMes = () => {
    if (mesAtual === 12) {
      setMesAtual(1);
      setAnoAtual(anoAtual + 1);
    } else {
      setMesAtual(mesAtual + 1);
    }
  };
  
  // Gerar dias do mês para o calendário
  const gerarDiasMes = () => {
    const primeiroDia = new Date(anoAtual, mesAtual - 1, 1);
    const ultimoDia = new Date(anoAtual, mesAtual, 0);
    const diasNoMes = ultimoDia.getDate();
    const diaInicio = primeiroDia.getDay();
    
    const dias = [];
    
    // Dias vazios antes do primeiro dia
    for (let i = 0; i < diaInicio; i++) {
      dias.push({ dia: null, eventos: [] });
    }
    
    // Dias do mês
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const eventosNoDia = eventos.filter(e => e.dia === dia);
      dias.push({ dia, eventos: eventosNoDia });
    }
    
    return dias;
  };
  
  // Formatar data
  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr + 'T00:00:00');
    return data.toLocaleDateString('pt-BR');
  };
  
  // Componente Card de Evento
  const EventoCard = ({ evento, compact = false }) => {
    const config = CATEGORIA_CONFIG[evento.categoria] || CATEGORIA_CONFIG.eventos_escolares;
    const Icon = config.icon;
    
    if (compact) {
      return (
        <div 
          className={`text-xs p-1 rounded truncate cursor-pointer ${config.bg} ${config.text}`}
          onClick={() => {
            setEventoSelecionado(evento);
            setShowDetalhes(true);
          }}
          title={evento.titulo}
        >
          {evento.destaque && <Star className="w-2 h-2 inline mr-1" />}
          {evento.titulo}
        </div>
      );
    }
    
    return (
      <Card 
        className={`hover:shadow-md transition-shadow border-l-4 ${config.border}`}
        data-testid={`evento-card-${evento.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-2 rounded-lg ${config.bgLight}`}>
                  <Icon className={`w-4 h-4 ${config.textLight}`} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    {evento.destaque && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                    {evento.titulo}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {formatarData(evento.data_inicio)}
                    {evento.data_fim && evento.data_fim !== evento.data_inicio && (
                      <> a {formatarData(evento.data_fim)}</>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${config.bg} ${config.text}`}>
                  {config.label}
                </Badge>
                {evento.publico_alvo?.filter(p => p !== 'geral').map(p => (
                  <Badge key={p} variant="outline" className="text-xs">
                    {PUBLICO_OPTIONS.find(o => o.value === p)?.label || p}
                  </Badge>
                ))}
              </div>
              
              {evento.descricao && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{evento.descricao}</p>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleToggleDestaque(evento)}
                title={evento.destaque ? 'Remover destaque' : 'Destacar'}
              >
                <Star className={`w-4 h-4 ${evento.destaque ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => abrirEdicao(evento)}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleDeletarEvento(evento.id)}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-green-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-purple-600" />
            Calendário Escolar 2026
          </h1>
          <p className="text-gray-500 text-sm">NOVO DNA Instituto de Aprendizagem</p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {eventos.length === 0 && (
            <Button variant="outline" onClick={handlePopularCalendario} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Popular Calendário 2026
            </Button>
          )}
          
          <Button onClick={() => setShowNovoEvento(true)} data-testid="btn-novo-evento">
            <Plus className="w-4 h-4 mr-2" />
            Novo Evento
          </Button>
        </div>
      </div>
      
      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <PartyPopper className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">Eventos Escolares</p>
                  <p className="text-xl font-bold text-purple-600">
                    {estatisticas.por_categoria?.eventos_escolares || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-xs text-gray-500">Feriados</p>
                  <p className="text-xl font-bold text-green-600">
                    {estatisticas.por_categoria?.feriados || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Umbrella className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-500">Suspensas/Férias</p>
                  <p className="text-xl font-bold text-orange-600">
                    {estatisticas.por_categoria?.atividades_suspensas || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Avaliações</p>
                  <p className="text-xl font-bold text-blue-600">
                    {estatisticas.por_categoria?.avaliacoes || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-xs text-gray-500">Em Destaque</p>
                  <p className="text-xl font-bold text-yellow-600">
                    {estatisticas.destaques || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filtros e Controles */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            {/* Busca */}
            <div className="relative flex-1 w-full md:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Buscar eventos..."
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Filtro categoria */}
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Modo de visualização */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button 
                variant={viewMode === 'mes' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('mes')}
              >
                <CalendarDays className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'lista' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('lista')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'cards' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('cards')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Navegação de mês */}
            {viewMode === 'mes' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={mesAnterior}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-medium min-w-32 text-center">
                  {MESES[mesAtual - 1]} {anoAtual}
                </span>
                <Button variant="outline" size="icon" onClick={proximoMes}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Conteúdo Principal */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <>
          {/* Visualização Mês (Calendário) */}
          {viewMode === 'mes' && (
            <Card>
              <CardContent className="p-4">
                {/* Cabeçalho dos dias */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DIAS_SEMANA.map(dia => (
                    <div key={dia} className="text-center font-medium text-gray-500 text-sm py-2">
                      {dia}
                    </div>
                  ))}
                </div>
                
                {/* Grid do calendário */}
                <div className="grid grid-cols-7 gap-1">
                  {gerarDiasMes().map((item, index) => (
                    <div 
                      key={index}
                      className={`min-h-24 border rounded-lg p-1 ${
                        item.dia ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                      }`}
                    >
                      {item.dia && (
                        <>
                          <div className="text-right text-sm font-medium text-gray-600 mb-1">
                            {item.dia}
                          </div>
                          <div className="space-y-1">
                            {item.eventos.slice(0, 3).map(evento => (
                              <EventoCard key={evento.id} evento={evento} compact />
                            ))}
                            {item.eventos.length > 3 && (
                              <p className="text-xs text-gray-500 text-center">
                                +{item.eventos.length - 3} mais
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Visualização Lista */}
          {viewMode === 'lista' && (
            <Card>
              <CardContent className="p-4">
                {eventos.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum evento encontrado</p>
                    <Button 
                      variant="link" 
                      className="mt-2"
                      onClick={handlePopularCalendario}
                    >
                      Popular calendário 2026
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {eventos.map(evento => (
                      <EventoCard key={evento.id} evento={evento} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Visualização Cards */}
          {viewMode === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventos.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum evento encontrado</p>
                  </CardContent>
                </Card>
              ) : (
                eventos.map(evento => (
                  <EventoCard key={evento.id} evento={evento} />
                ))
              )}
            </div>
          )}
        </>
      )}
      
      {/* Legenda */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Legenda:</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${config.bg}`} />
                  <Icon className={`w-4 h-4 ${config.textLight}`} />
                  <span className="text-sm text-gray-600">{config.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Modal Novo Evento */}
      <Dialog open={showNovoEvento} onOpenChange={setShowNovoEvento}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Novo Evento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Título do Evento *</Label>
              <Input 
                placeholder="Ex: Reunião de Pais"
                value={formEvento.titulo}
                onChange={(e) => setFormEvento(prev => ({ ...prev, titulo: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Início *</Label>
                <Input 
                  type="date"
                  value={formEvento.data_inicio}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data de Fim (opcional)</Label>
                <Input 
                  type="date"
                  value={formEvento.data_fim}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Categoria</Label>
              <Select 
                value={formEvento.categoria}
                onValueChange={(v) => setFormEvento(prev => ({ ...prev, categoria: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${config.bg}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Local</Label>
                <Input 
                  placeholder="Ex: Auditório"
                  value={formEvento.local}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, local: e.target.value }))}
                />
              </div>
              <div>
                <Label>Horário</Label>
                <Input 
                  placeholder="Ex: 19h às 21h"
                  value={formEvento.horario}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, horario: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea 
                placeholder="Descrição detalhada do evento..."
                value={formEvento.descricao}
                onChange={(e) => setFormEvento(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div>
              <Label>Público-alvo</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PUBLICO_OPTIONS.map(opt => (
                  <Badge 
                    key={opt.value}
                    variant={formEvento.publico_alvo.includes(opt.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormEvento(prev => ({
                        ...prev,
                        publico_alvo: prev.publico_alvo.includes(opt.value)
                          ? prev.publico_alvo.filter(p => p !== opt.value)
                          : [...prev.publico_alvo, opt.value]
                      }));
                    }}
                  >
                    {opt.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch 
                checked={formEvento.destaque}
                onCheckedChange={(v) => setFormEvento(prev => ({ ...prev, destaque: v }))}
              />
              <Label className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Marcar como destaque
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNovoEvento(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCriarEvento}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Evento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal Editar Evento */}
      <Dialog open={showEditarEvento} onOpenChange={setShowEditarEvento}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-600" />
              Editar Evento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Título do Evento *</Label>
              <Input 
                value={formEvento.titulo}
                onChange={(e) => setFormEvento(prev => ({ ...prev, titulo: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Início *</Label>
                <Input 
                  type="date"
                  value={formEvento.data_inicio}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data de Fim</Label>
                <Input 
                  type="date"
                  value={formEvento.data_fim}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Categoria</Label>
              <Select 
                value={formEvento.categoria}
                onValueChange={(v) => setFormEvento(prev => ({ ...prev, categoria: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${config.bg}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Local</Label>
                <Input 
                  value={formEvento.local}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, local: e.target.value }))}
                />
              </div>
              <div>
                <Label>Horário</Label>
                <Input 
                  value={formEvento.horario}
                  onChange={(e) => setFormEvento(prev => ({ ...prev, horario: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea 
                value={formEvento.descricao}
                onChange={(e) => setFormEvento(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div>
              <Label>Público-alvo</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PUBLICO_OPTIONS.map(opt => (
                  <Badge 
                    key={opt.value}
                    variant={formEvento.publico_alvo.includes(opt.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setFormEvento(prev => ({
                        ...prev,
                        publico_alvo: prev.publico_alvo.includes(opt.value)
                          ? prev.publico_alvo.filter(p => p !== opt.value)
                          : [...prev.publico_alvo, opt.value]
                      }));
                    }}
                  >
                    {opt.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch 
                checked={formEvento.destaque}
                onCheckedChange={(v) => setFormEvento(prev => ({ ...prev, destaque: v }))}
              />
              <Label className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Marcar como destaque
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarEvento(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAtualizarEvento}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal Detalhes do Evento */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-md">
          {eventoSelecionado && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {eventoSelecionado.destaque && <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />}
                  {eventoSelecionado.titulo}
                </DialogTitle>
                <DialogDescription>
                  {formatarData(eventoSelecionado.data_inicio)}
                  {eventoSelecionado.data_fim && eventoSelecionado.data_fim !== eventoSelecionado.data_inicio && (
                    <> a {formatarData(eventoSelecionado.data_fim)}</>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <Badge className={`${CATEGORIA_CONFIG[eventoSelecionado.categoria]?.bg || 'bg-gray-500'} text-white`}>
                  {CATEGORIA_CONFIG[eventoSelecionado.categoria]?.label || eventoSelecionado.categoria}
                </Badge>
                
                {eventoSelecionado.descricao && (
                  <p className="text-gray-600">{eventoSelecionado.descricao}</p>
                )}
                
                {eventoSelecionado.local && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {eventoSelecionado.local}
                  </div>
                )}
                
                {eventoSelecionado.horario && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    {eventoSelecionado.horario}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  {eventoSelecionado.publico_alvo?.map(p => 
                    PUBLICO_OPTIONS.find(o => o.value === p)?.label || p
                  ).join(', ')}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetalhes(false)}>
                  Fechar
                </Button>
                <Button onClick={() => {
                  setShowDetalhes(false);
                  abrirEdicao(eventoSelecionado);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
